import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  View,
  FlatList,
  TouchableOpacity,
  Image,
  Text,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { toast } from "react-toastify";
import { AppColors } from "../../../../utility/AppColors";
import ChatModalMessageBubble from "../../../../Components/chat-modal-common-components/ChatModalMessageBubble";
import Separator from "../../../../Components/Separator";
import ChatMessageInput from "../../../../Components/chat-common-components/ChatMessageInput";
import ChatModalHeader from "../../../../Components/chat-modal-common-components/ChatModalHeader";
import ChatModalProfileSection from "../../../../Components/chat-modal-common-components/ChatModalProfileSection";
import { useChat } from "../../../../context/ChatContext";
import { fetchChatHistoryID } from "../../../../services/chatAPI";
import { AppImages } from "../../../../utility/AppImages";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import DeleteConfirmationModal from "../../../../Components/chat-common-components/DeleteConfirmationModal";

interface ChattingModalProps {
  chatID: string;
  selectedName: string;
  isVisible: boolean;
  onClose: () => void;
}

const ChattingModal: React.FC<ChattingModalProps> = ({
  chatID,
  selectedName,
  isVisible,
  onClose,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const {
    chatData,
    setChatData,
    fetchUserChats,
    currentChat,
    userData,
    sendMessage,
    userDataRef,
    setChats,
    deleteMessageForEveryone,
    deleteMessageForUser,
  } = useChat();

  const [newMessage, setNewMessage] = useState("");
  const flatListRef = useRef<FlatList>(null);
  const [showLoader, setShowLoader] = useState(false);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

  useEffect(() => {
    if (chatID) {
      fetchChatHistory();
    }
  }, [chatID]);

  const fetchChatHistory = async () => {
    if (!chatID || !userData?.accessToken) return;
    setShowLoader(true);
    try {
      const response = await fetchChatHistoryID(userData.accessToken, chatID);
      setShowLoader(false);
      if (response.statusCode === 200) {
        const sortedMessages = response.body.messages.sort(
          (a, b) => b.timestamp - a.timestamp
        );
        setChatData(sortedMessages || []);
      } else {
        toast.error("Error fetching chat history");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error("Error fetching chat history");
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && userData && currentChat) {
      sendMessage(
        currentChat.conversationId,
        userData.username,
        newMessage.trim(),
        userData.accessToken
      );
      setNewMessage("");
    }
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedMessages([]);
  };

  const handleDeletePress = () => {
    setSelectionMode(true);
  };

  const handleCheckboxToggle = (messageId) => {
    setSelectedMessages((prevSelected) =>
      prevSelected.includes(messageId)
        ? prevSelected.filter((id) => id !== messageId)
        : [...prevSelected, messageId]
    );
  };
  const onDeleteForEveryone = () => {
    selectedMessages.forEach((messageId) => {
      const message = chatData.find((msg) => msg.messageId === messageId);

      if (message) {
        if (userDataRef.current.username === message.userId) {
          deleteMessageForEveryone(message.conversationId, message.messageId);
          //@ts-ignore
          setChatData((prevChatData: ChatMessage[]) =>
            prevChatData.filter((msg) => msg.messageId !== messageId)
          );

          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.conversationId === message.conversationId &&
              chat.lastMessageId === messageId
                ? {
                    ...chat,
                    lastMessage: "This message was deleted",
                    lastMessageTimestamp: null,
                  }
                : chat
            )
          );
        }
      }
    });

    setDeleteModalVisible(false);
    setSelectionMode(false);
    setSelectedMessages([]);
  };

  const onDeleteForMe = () => {
    selectedMessages.forEach((messageId) => {
      const message = chatData.find((msg) => msg.messageId === messageId);

      if (message) {
        deleteMessageForUser(message.conversationId, message.messageId);
        //@ts-ignore
        setChatData((prevChatData: ChatMessage[]) =>
          prevChatData.filter((msg: any) => msg.messageId !== messageId)
        );

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.conversationId === message.conversationId &&
            chat.lastMessageId === messageId
              ? {
                  ...chat,
                  lastMessage: null,
                  lastMessageTimestamp: null,
                }
              : chat
          )
        );
      }
    });

    setDeleteModalVisible(false);
    setSelectionMode(false);
    setSelectedMessages([]);
  };
  const renderChatMessages = ({ item }) => {
    const isSender = item.userId === userData?.username;
    const otherMembersRead = currentChat.members
      .filter((member) => member !== userData.username)
      .every((member) => item.readStatus?.[member]);

    const messageId = item.messageId;
    const isSelected = selectedMessages.includes(messageId);

    return (
      <TouchableOpacity
        style={{
          flexDirection: "row",
          justifyContent: isSender
            ? selectionMode
              ? "space-between"
              : "flex-end"
            : "flex-start",
          paddingVertical: 5,
          paddingHorizontal: 10,
        }}
        onPress={() => handleCheckboxToggle(messageId)}
        activeOpacity={0.7}
      >
        {selectionMode && (
          <View
            style={[
              styles.checkboxContainer,
              isSelected && styles.checkedCheckbox,
            ]}
          >
            {isSelected && (
              <Image
                source={AppImages.icRightIcon}
                style={styles.checkmarkIcon}
              />
            )}
          </View>
        )}

        <ChatModalMessageBubble
          item={item}
          isRead={otherMembersRead}
          message={item.messageContent}
          isSender={item.userId === userData?.username}
          timestamp={item.timestamp}
          user={item.userId}
          currentChat={currentChat}
          handleDeletePress={handleDeletePress}
          selectionMode={selectionMode}
          selectedMessages={selectedMessages}
          setSelectedMessages={setSelectedMessages}
        />
      </TouchableOpacity>
    );
  };
  return (
    <>
      <View style={{ marginTop: 20, height: "86%" }}>
        <Modal visible={isVisible} onRequestClose={onClose}>
          <View style={styles.chatContainer}>
            <ChatModalHeader
              deviceSize={deviceSize}
              currentChat={currentChat}
              selectionMode={selectionMode}
            />
            <View style={styles.chatWrapper}>
              <ChatModalProfileSection
                item={currentChat}
                selectedName={selectedName}
                selectionMode={selectionMode}
                handleCancelSelection={handleCancelSelection}
              />
              <Separator
                color={AppColors.grayColor_333333}
                marginHorizontal={4}
                height={2}
                marginTop={10}
                marginBottom={20}
              />
              <FlatList
                data={chatData}
                inverted
                renderItem={renderChatMessages}
                ref={flatListRef}
              />
              {selectionMode ? (
                <View style={styles.bottomBar}>
                  <TouchableOpacity onPress={() => setDeleteModalVisible(true)}>
                    <Image
                      source={AppImages.icDelete}
                      style={{
                        height: 24,
                        width: 24,
                        resizeMode: "contain",
                      }}
                    />
                  </TouchableOpacity>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_16,
                      {
                        color: AppColors.black_121212,
                      },
                    ]}
                  >
                    {selectedMessages.length} Selected
                  </Text>
                </View>
              ) : (
                <>
                  <Separator
                    color={AppColors.white}
                    marginHorizontal={4}
                    height={3}
                    marginTop={10}
                    marginBottom={10}
                  />
                  <ChatMessageInput
                    newMessage={newMessage}
                    setNewMessage={setNewMessage}
                    handleSendMessage={handleSendMessage}
                  />
                </>
              )}
            </View>
          </View>
        </Modal>
      </View>

      {isDeleteModalVisible && (
        <DeleteConfirmationModal
          chatContainerRef={""}
          selectedMessages={selectedMessages}
          isVisible={isDeleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          onDeleteForEveryone={onDeleteForEveryone}
          onDeleteForMe={onDeleteForMe}
        />
      )}
    </>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    chatContainer: {
      flex: 1,
      backgroundColor: "black",
    },
    chatWrapper: {
      backgroundColor: AppColors.gray_1F1F1F,
      borderRadius: 10,
      borderColor: AppColors.grayColor_282828,
      borderWidth: 1,
      padding: 10,
      marginBottom: 15,
      flex: 1,
      marginHorizontal: 10,
    },
    checkboxContainer: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: AppColors.white,
      alignItems: "center",
      justifyContent: "center",
      marginRight: 10,
      marginTop: 10,
    },
    checkedCheckbox: {
      backgroundColor: AppColors.green_32CD32,
    },
    checkmarkIcon: {
      width: 12,
      height: 12,
      tintColor: AppColors.white,
    },
    bottomBar: {
      height: 50,
      backgroundColor: AppColors.grayColor_D9D9D9,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 20,
      marginLeft: -10,
      marginBottom: -10,
      marginRight: -10,
      marginTop: 20,
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      chatWrapper: {
        padding: 8,
      },
    },
    [DEVICE_SIZES.SM]: {
      chatWrapper: {
        padding: 9,
      },
    },
  }
);

export default ChattingModal;
